import { useCallback, createElement } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { focusOutline } from '@hivebrite/design-system/Theme';
import { Tooltip } from '@hivebrite/design-system/Tooltip';

import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

export interface ToolbarItemProps {
  ariaLabel?: string;
  children?: React.ReactNode;
  className?: string;
  css?: any;
  dataTestId?: string;
  disabled?: boolean;
  enableFontAwesomeIcon?: boolean;
  icon?: any;
  iconSize?: number;
  id?: string;
  isActive?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  style?: any;
  tooltip?: string;
}

const ToolbarItem = ({
  className,
  ariaLabel,
  children,
  css,
  dataTestId,
  id,
  icon,
  iconSize = 20,
  isActive,
  disabled,
  onClick,
  style,
  onMouseDown,
  tooltip,
  enableFontAwesomeIcon,
}: ToolbarItemProps) => {
  const handleMouseDown = useCallback(
    (e) => {
      // Only trigger with mouse left click
      if (onClick && e.button === 0) {
        onClick(e);
      }
    },
    [onClick],
  );
  const handleKeyPress = useCallback(
    (e) => {
      e.preventDefault();

      onEnterOrSpacePress(onClick)(e);
    },
    [onClick],
  );

  const hasChildren = !!children;

  const content = (
    <>
      {enableFontAwesomeIcon
        ? icon
        : icon && (
            <Box
              color="inherit"
              height={iconSize}
              ml={hasChildren ? -1 : undefined}
              mr={hasChildren ? 2 : undefined}
              width={iconSize}
            >
              {createElement(icon, {
                height: '100%',
                width: '100%',
                // @hivebrite/design-system icons use `size` props instead of the `width` and `height` ones.
                size: '100%',
              })}
            </Box>
          )}
      {children}
    </>
  );

  return (
    <ToolbarItemWrapper
      aria-disabled={disabled}
      aria-label={ariaLabel}
      css={css}
      data-testid={dataTestId}
      id={id}
      $isActive={isActive}
      $isDisabled={disabled}
      onMouseDown={onMouseDown}
      onClick={handleMouseDown}
      onKeyPress={handleKeyPress}
      style={style}
      tabIndex={disabled ? -1 : 0}
      role={disabled ? undefined : 'button'}
      className={className}
    >
      {tooltip ? (
        <Tooltip childrenElement={tooltip || ariaLabel}>{content}</Tooltip>
      ) : (
        content
      )}
    </ToolbarItemWrapper>
  );
};

export default ToolbarItem;

export const ToolbarItemWrapper = styled(Box).attrs(
  ({ $isActive, theme }: { $isActive?: boolean; theme: any }) => ({
    alignItems: 'center',
    justifyContent: 'center',
    color: $isActive ? 'primary' : theme.input.color,
  }),
)<{ $isActive?: boolean; $isDisabled?: boolean }>`
  ${focusOutline}

  cursor: ${({ $isDisabled }) => ($isDisabled ? 'default' : 'pointer')};
  height: 23px;
  min-width: 23px;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'all')};
  user-select: none;
  white-space: nowrap;

  &:focus,
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;
